import { Button, Label, SubmitButton } from "@xola/ui-kit";
import { FormField, InputField } from "common";
import { sortBy } from "lodash";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
    Affiliate,
    PartnerDemographicTerm,
    ProductObject,
    PurchaseRuleLink,
    SelectionState,
    TermsTemplate,
    TermsType,
} from "types";
import { ScrollToFieldError } from "../../../helpers/validationHelper";
import { useProducts } from "../../../hooks/resources/useProduct";
import { useModal } from "../../../hooks/useVisibility";
import { ProductSelectorModal } from "../../Common/ProductSelectorModal";
import { PromptModal } from "../../Common/PromptModal";
import { RadioInput } from "../../Common/RadioInput";
import { SelectedProductListInput } from "../../Common/SelectedProductListInput";
import { XolaFormik } from "../../Common/XolaFormik";
import { ContentLayout } from "../../Layout/ContentLayout";
import { AdvancedTermsForm } from "./AdvancedTermsForm";
import { BasicTermsForm } from "./BasicTermsForm";
import { ProductTermExceptionFormTable } from "./ProductTermExceptionFormTable";
import {
    generatePartnerDemographicTermsFromDiscountCommission,
    termTemplateValidationSchema,
} from "./TermsTemplateFormValues";

const confirmLeaveModalCopy = {
    [TermsType.BASIC]:
        "You are about to switch from advanced to basic terms. This action will result in all settings being lost. Are you sure you want to continue?",
    [TermsType.ADVANCED]:
        "You are about to switch from basic to advanced terms. Only the basic discount and commissions will be copied over to all products, but the term exceptions would be lost. Are you sure you want to continue?",
};

interface TermTemplateFormProps {
    values: TermsTemplate;
    isSaving: boolean;
    showAgentFormOptions?: boolean;
    termsTemplate?: PurchaseRuleLink;
    linkedAgents?: Array<Affiliate | undefined>;
    handleFormSubmit: (values: TermsTemplate) => void;
    setIsSaveAsNew: (value: boolean) => void;
}

export const TermTemplateForm = ({
    values,
    isSaving,
    showAgentFormOptions,
    termsTemplate,
    linkedAgents,
    setIsSaveAsNew,
    handleFormSubmit,
}: TermTemplateFormProps) => {
    const [isProductSelectorOpen, setIsProductSelectorOpen] = useState(false);
    const termSwitchPromptModal = useModal();
    const [desiredTermsType, setDesiredTermsType] = useState<TermsType | null>(null);
    const history = useHistory();
    const { experiences, packages } = useProducts();

    return (
        <XolaFormik
            enableReinitialize
            validationSchema={termTemplateValidationSchema}
            initialValues={values}
            onSubmit={handleFormSubmit}
        >
            {({ handleSubmit, setFieldValue, errors, values, submitCount, dirty, setValues }) => {
                const handleCancelClick = () => {
                    if (showAgentFormOptions) {
                        history.push("/distribution/agents");
                    } else {
                        history.push("/distribution/termsTemplates");
                    }
                };

                const handleUpdateClick = (isSaveAsNew: boolean) => {
                    setIsSaveAsNew(isSaveAsNew);
                    handleSubmit();
                };

                const handleProductSelectorConfirm = (value: SelectionState) => {
                    // For terms type advanced, we need to get all the product ids if all is selected.
                    // Introduce a new field `data` to store the product ids.
                    let productIds = value.selected;
                    if (value.all && values.termsType === TermsType.ADVANCED) {
                        productIds = [...experiences, ...packages].map((product) => product.id) ?? [];
                    }

                    const selectedProducts = sortBy(value.selected);

                    const partnerDemographicTerms = productIds.map((id) => {
                        return (
                            values.partnerDemographicTerms?.find(
                                (partnerDemographicTerm: PartnerDemographicTerm) =>
                                    partnerDemographicTerm.product.id === id,
                            ) ?? {
                                // initial form value if not exists
                                product: { id: id },
                                partnerDemographicPrices: [],
                            }
                        );
                    });

                    setValues({
                        ...values,
                        products: { all: value.all, selected: selectedProducts, data: productIds },
                        partnerDemographicTerms,
                        termsType: values.termsType,
                    });
                };

                const handleAdvanceTermDeleteClick = (productIdToRemove: ID) => {
                    let productIds = values.products.selected;
                    if (values.products.all && values.termsType === TermsType.ADVANCED && values.products.data) {
                        productIds = values.products.data ?? [];
                    }

                    const selectedProductIds = productIds.filter((productId) => productId !== productIdToRemove);

                    handleProductSelectorConfirm({
                        all: false,
                        selected: selectedProductIds.length > 0 ? selectedProductIds : [],
                    });
                };

                const shouldShowTermSwitchWarning = (termsType: TermsType) => {
                    return (
                        (termsType === TermsType.ADVANCED && values.productTermExceptions.length > 0) ||
                        (termsType === TermsType.BASIC && values.partnerDemographicTerms.length > 0)
                    );
                };

                const handleTermsTypeChange = (termsType: TermsType) => {
                    if (shouldShowTermSwitchWarning(termsType)) {
                        setDesiredTermsType(termsType);
                        termSwitchPromptModal.toggle();
                    } else {
                        // do not show a warning if values are not set.
                        handleTermSwitchConfirm(termsType);
                    }
                };

                const handleTermSwitchConfirm = (termsType: TermsType) => {
                    // Switching from advanced to basic
                    if (termsType === TermsType.BASIC) {
                        // all configurations will be lost.

                        setFieldValue("partnerDemographicTerms", []);
                    } else if (termsType === TermsType.ADVANCED) {
                        let selectedProductIds = values.products.selected;
                        if (values.products.all) {
                            selectedProductIds = [...experiences, ...packages].map((product) => product.id) ?? [];
                            values.products.data = selectedProductIds;
                        }

                        const partnerDemographicTerms = generatePartnerDemographicTermsFromDiscountCommission(
                            { ...values, termsType },
                            [...experiences, ...packages],
                        );

                        setFieldValue("partnerDemographicTerms", partnerDemographicTerms);
                    }

                    setFieldValue("termsType", termsType);
                    setDesiredTermsType(null);
                    termSwitchPromptModal.hide();
                };

                return (
                    <ContentLayout isWide className="space-y-6" isDisabled={isSaving}>
                        <h2>Terms Template</h2>

                        <div className="flex flex-col space-x-4 divide-y divide-gray-light">
                            <div className="flex space-x-10 py-12">
                                <div className="w-1/4">
                                    <h3>Terms</h3>
                                    <p className="text-gray-darker">
                                        Choose which products you want your Partner&apos;s terms to apply to.
                                    </p>
                                </div>
                                <div className="w-1/2 pl-8">
                                    <FormField name="title" label="Title">
                                        <InputField placeholder="Title" />
                                    </FormField>

                                    <div className="space-y-2" data-testid="edit-terms-template-products-button">
                                        <SelectedProductListInput
                                            selectedProducts={values.products}
                                            onEditClick={() => setIsProductSelectorOpen(true)}
                                        />

                                        <ProductSelectorModal
                                            enableSelectAll
                                            title="Product Restrictions"
                                            description="Restrict the products where the discount applies."
                                            objects={[ProductObject.EXPERIENCE, ProductObject.PACKAGE]}
                                            isOpen={isProductSelectorOpen}
                                            value={values.products}
                                            onClose={() => setIsProductSelectorOpen(false)}
                                            onConfirm={handleProductSelectorConfirm}
                                        />
                                    </div>

                                    <Label>Terms</Label>
                                    <div className="flex space-x-6">
                                        <RadioInput
                                            checked={values.termsType === TermsType.BASIC}
                                            onChange={() => handleTermsTypeChange(TermsType.BASIC)}
                                        >
                                            Basic
                                        </RadioInput>

                                        <RadioInput
                                            checked={values.termsType === TermsType.ADVANCED}
                                            onChange={() => handleTermsTypeChange(TermsType.ADVANCED)}
                                        >
                                            Advanced
                                        </RadioInput>
                                    </div>
                                </div>
                            </div>
                            {values.termsType === TermsType.BASIC ? (
                                <>
                                    <BasicTermsForm
                                        values={values}
                                        errors={submitCount > 0 && !isSaving ? errors : undefined}
                                        setFieldValue={setFieldValue}
                                    />

                                    <div className="flex space-x-10 py-12">
                                        <div className="w-1/4">
                                            <h3>Commercial Terms Exceptions</h3>
                                            <p className="text-gray-darker">
                                                Product level overrides to the commission and discount
                                            </p>
                                        </div>
                                        <div className="w-1/2 pl-8">
                                            {/* Only show product term exceptions when terms type is basic */}
                                            <ProductTermExceptionFormTable
                                                values={values}
                                                experiences={experiences}
                                                packages={packages}
                                                linkedAgents={linkedAgents}
                                                setFieldValue={setFieldValue}
                                            />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <AdvancedTermsForm
                                    values={values}
                                    errors={submitCount > 0 && !isSaving ? errors : undefined}
                                    onDeleteTerm={handleAdvanceTermDeleteClick}
                                    onChange={async (value) => setFieldValue("partnerDemographicTerms", value)}
                                />
                            )}
                        </div>

                        <PromptModal
                            isOpen={termSwitchPromptModal.isVisible}
                            onConfirm={() => handleTermSwitchConfirm(desiredTermsType!)}
                            onClose={() => {
                                termSwitchPromptModal.hide();
                                setDesiredTermsType(null);
                            }}
                        >
                            {confirmLeaveModalCopy[desiredTermsType ?? TermsType.BASIC]}
                        </PromptModal>

                        <ContentLayout.Footer className="space-x-3">
                            {showAgentFormOptions && termsTemplate?.name ? (
                                <>
                                    <SubmitButton
                                        type="submit"
                                        disabled={!dirty}
                                        isLoading={isSaving}
                                        onClick={() => handleUpdateClick(false)}
                                    >
                                        Update{" "}
                                        {termsTemplate.name.length < 40 ? (
                                            <var data-var="termsTemplateName">{termsTemplate.name}</var>
                                        ) : (
                                            "Current"
                                        )}{" "}
                                        Template
                                    </SubmitButton>
                                    <SubmitButton
                                        type="submit"
                                        variant="outline"
                                        color="secondary"
                                        disabled={!dirty}
                                        isLoading={isSaving}
                                        onClick={() => handleUpdateClick(true)}
                                    >
                                        Save as New Template
                                    </SubmitButton>
                                </>
                            ) : (
                                <SubmitButton
                                    type="submit"
                                    isLoading={isSaving}
                                    disabled={!dirty}
                                    onClick={handleSubmit}
                                >
                                    Save
                                </SubmitButton>
                            )}
                            <Button type="reset" variant="outline" color="secondary" onClick={handleCancelClick}>
                                Cancel
                            </Button>
                        </ContentLayout.Footer>

                        <ScrollToFieldError />
                    </ContentLayout>
                );
            }}
        </XolaFormik>
    );
};
